/**
 * 팝업 중앙 모달 (CENTER_MODAL).
 *
 * dismiss 정책:
 *  - "닫기" 버튼 / X → 세션 dismiss (탭 닫으면 다시 표시)
 *  - "오늘 하루 창 열지 않기" 버튼 → 24h dismiss
 *  - "일주일간 창 열지 않기" 버튼 → 168h dismiss
 *  - "다시 보지 않기" 버튼 → 영구 dismiss (어드민 허용 시)
 *
 * 영구지침: docs/develop/popup/popup-spec.md §9.4
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import type { PopupResponse } from '@/features/popup/types'
import { recordClick, recordDismiss, recordImpression } from '@/features/popup/api'
import PopupMarkdown from './PopupMarkdown'
import {
  getDismissOptions,
  markDismissedFor,
} from '@/lib/popup/dismissStorage'

interface Props {
  popup: PopupResponse
}

export default function CenterModal({ popup }: Props) {
  const navigate = useNavigate()
  const [visible, setVisible] = useState(true)
  const dialogRef = useRef<HTMLDivElement>(null)
  const ctaButtonRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const dismissOptions = useMemo(
    () => (popup.allowDismiss ? getDismissOptions(popup.dismissCooldownHours) : []),
    [popup.allowDismiss, popup.dismissCooldownHours]
  )

  useEffect(() => {
    void recordImpression(popup.id)
  }, [popup.id])

  useEffect(() => {
    if (!visible) return
    ctaButtonRef.current?.focus() || closeButtonRef.current?.focus()

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && popup.allowDismiss) {
        e.preventDefault()
        handleClose()
        return
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return
        const first = focusable[0]!
        const last = focusable[focusable.length - 1]!
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  if (!visible) return null

  const handleClick = () => {
    void recordClick(popup.id)
    if (popup.ctaUrl) navigate(popup.ctaUrl)
    setVisible(false)
  }

  /** X 또는 "닫기" → 세션 dismiss */
  const handleClose = () => {
    if (!popup.allowDismiss) return
    void recordDismiss(popup.id)
    // 그냥 닫기(X/배경/Esc)는 저장하지 않는다 → 새로고침·다음 방문 시 다시 노출.
    // 기간/영구 억제는 아래 "오늘 하루 / 일주일 / 다시 보지 않기" 버튼(handleDismissFor)에서만.
    setVisible(false)
  }

  /** 기간/영구 dismiss 버튼 클릭 */
  const handleDismissFor = (hours: number | null) => {
    void recordDismiss(popup.id)
    markDismissedFor(popup.id, hours)
    setVisible(false)
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`popup-${popup.id}-title`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-10"
      onClick={(e) => {
        if (e.target === e.currentTarget && popup.allowDismiss) handleClose()
      }}
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
      >
        {popup.allowDismiss && (
          <button
            type="button"
            onClick={handleClose}
            aria-label="닫기"
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {popup.imageUrl && (
          <img
            src={popup.imageUrl}
            alt=""
            className="mb-4 w-full rounded-xl object-cover"
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).style.display = 'none'
            }}
          />
        )}

        <h2
          id={`popup-${popup.id}-title`}
          className="text-lg font-bold text-slate-900"
        >
          {popup.title}
        </h2>

        {popup.body && (
          <PopupMarkdown className="mt-2 text-sm leading-relaxed text-slate-600" >
            {popup.body}
          </PopupMarkdown>
        )}

        {popup.ctaLabel && (
          <div className="mt-5">
            <button
              ref={ctaButtonRef}
              type="button"
              onClick={handleClick}
              className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {popup.ctaLabel}
            </button>
          </div>
        )}

        {/* dismiss 버튼 한 줄 (오늘 하루 | 일주일 | 닫기) */}
        {popup.allowDismiss && (
          <div className="mt-4 flex items-center justify-center gap-1 border-t border-slate-100 pt-3">
            {dismissOptions.map((option, i) => (
              <button
                key={option.hours ?? 'permanent'}
                ref={i === 0 && !popup.ctaLabel ? closeButtonRef : undefined}
                type="button"
                onClick={() => handleDismissFor(option.hours)}
                className="px-2.5 py-1.5 text-xs text-slate-500 transition hover:text-slate-700"
              >
                {option.label}
              </button>
            ))}
            {dismissOptions.length > 0 && (
              <span className="text-slate-200">|</span>
            )}
            <button
              ref={!popup.ctaLabel && dismissOptions.length === 0 ? closeButtonRef : undefined}
              type="button"
              onClick={handleClose}
              className="px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:text-slate-900"
            >
              닫기
            </button>
          </div>
        )}

        {!popup.allowDismiss && (
          <p className="mt-3 text-center text-xs text-slate-400">
            중요한 안내라 닫을 수 없어요
          </p>
        )}
      </div>
    </div>
  )
}
